import React from "react";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import Badge from "../../../../../../components/common/PL/Badge/Badge";
import Button from "../../../../../../components/common/PL/Button/Button";
import PersonaEstadoStatus from "./PersonaEstadoStatus";
import AutorizadoresModal from "./AutorizadoresModal";
import { COMMUNICATION_METHOD_LABELS } from "./RegistrarAutorizacionModal";
import { AuthorizationGroup, AuthorizationRow } from "./transportAuthorizationTypes";
import { formatDateTimeShort, isWithinRange } from "./transportDateFormat";

type AutorizacionDetailProps = {
  group: AuthorizationGroup;
  selectedRowId: string | null;
  onSelectRow: (rowId: string) => void;
  onResendRow: (rowId: string) => void;
  /** Ausente en las vistas de sólo lectura (tarjeta del acompañante): sin handler no se ofrece
   * registrar la autorización por otro medio. */
  onRegistrarPorOtroMedio?: (rowId: string) => void;
  disabled?: boolean;
};

/** El dock puede venir null, undefined o "" según cómo se haya cargado la autorización. */
const hasDock = (dock: AuthorizationRow["dock"]) =>
  dock !== null && dock !== undefined && dock !== "";

const ROW_STATUS_CLASSES: Partial<Record<AuthorizationRow["status"], string>> = {
  pending: "transportPersonaRowPendiente",
  rejected: "transportPersonaRowRechazada",
  approved: "transportPersonaRowAprobada",
};

type VigenciaRowProps = {
  row: AuthorizationRow;
  isValid: boolean;
  onResendRow: (rowId: string) => void;
};

// Vigencia (a la izquierda) y estado de la autorización + reenviar (a la derecha, alineados
// debajo de los badges de información) comparten la misma línea.
const VigenciaRow: React.FC<VigenciaRowProps> = ({ row, isValid, onResendRow }) => {
  const hasVigencia = !!row.validFrom && !!row.validTo;
  // Pendiente/rechazada: la ventana de vigencia todavía no es relevante, así que el badge
  // Vigente/Fuera de vigencia no se muestra (sólo aprobada y los estados terminales lo usan).
  const showVigenteBadge = row.status !== "pending" && row.status !== "rejected";

  return (
    <div className="transportPersonaVigenciaRow">
      <div className="transportPersonaVigenciaInfo">
        {hasVigencia && (
          <>
            <span className={!isValid ? "transportVigenciaVencida" : undefined}>
              Vigencia: {formatDateTimeShort(row.validFrom)} – {formatDateTimeShort(row.validTo)}
            </span>
            {showVigenteBadge && (
              <Badge
                variant={isValid ? "green" : "red"}
                label={isValid ? "Vigente" : "Fuera de vigencia"}
              />
            )}
          </>
        )}
      </div>
      <PersonaEstadoStatus
        status={row.status}
        requestedAt={row.requestedAt}
        isValid={isValid}
        onReenviar={() => onResendRow(row.id)}
        pendingLabel={row.isDockRequest ? "Dock pendiente de asignación" : undefined}
      />
    </div>
  );
};

/** Detalle expandible de una solicitud aprobada. Reusa tal cual el contenedor y la grilla
 * label/valor de las tarjetas de egreso; el modificador sólo agrega la línea divisoria. */
const SolicitudDetalle: React.FC<{ row: AuthorizationRow }> = ({ row }) => {
  const { communicationMethod, authorizedBy } = row.guardApproval;

  return (
    <div className="transportEgresoDetailContainer transportPersonaDetailAccordion">
      <div className="transportEgresoDetailGrid">
        <div className="transportEgresoDetailRow">
          <span className="transportEgresoDetailLabel">Patente vehículo (tractor)</span>
          {row.vehiclePlate ? (
            <span className="transportPatenteChip">{row.vehiclePlate}</span>
          ) : (
            <span className="transportEgresoDetailNoAplica">Sin patente</span>
          )}
        </div>

        {row.trailerPlate && (
          <div className="transportEgresoDetailRow">
            <span className="transportEgresoDetailLabel">Patente acoplado</span>
            <span className="transportPatenteChip">{row.trailerPlate}</span>
          </div>
        )}

        {hasDock(row.dock) && (
          <div className="transportEgresoDetailRow">
            <span className="transportEgresoDetailLabel">N° de dock</span>
            <span className="transportEgresoDetailValue">{row.dock}</span>
          </div>
        )}

        {communicationMethod && (
          <div className="transportEgresoDetailRow">
            <span className="transportEgresoDetailLabel">Medio de autorización</span>
            <span className="transportEgresoDetailValue">
              {COMMUNICATION_METHOD_LABELS[communicationMethod]}
            </span>
          </div>
        )}

        {authorizedBy && (
          <div className="transportEgresoDetailRow">
            <span className="transportEgresoDetailLabel">Responsable que autorizó</span>
            <span className="transportEgresoDetailValue">{authorizedBy}</span>
          </div>
        )}
      </div>

      {row.gateObservations && (
        <div className="transportObsBox">
          <span className="transportObsBoxLabel">Observaciones</span>
          <span className="transportObsBoxText">{row.gateObservations}</span>
        </div>
      )}
    </div>
  );
};

const AutorizacionDetail: React.FC<AutorizacionDetailProps> = ({
  group,
  selectedRowId,
  onSelectRow,
  onResendRow,
  onRegistrarPorOtroMedio,
  disabled = false,
}) => {
  const [authorizersModalRow, setAuthorizersModalRow] = React.useState<AuthorizationRow | null>(
    null
  );
  const [expandedRowId, setExpandedRowId] = React.useState<string | null>(null);
  const previousRowStatusesRef = React.useRef<Record<string, AuthorizationRow["status"]>>({});

  React.useEffect(() => {
    const newlyApprovedRequest = group.rows.find(
      (row) =>
        row.kind === "request" &&
        row.status === "approved" &&
        previousRowStatusesRef.current[row.id] !== "approved"
    );

    previousRowStatusesRef.current = group.rows.reduce<Record<string, AuthorizationRow["status"]>>(
      (statuses, row) => {
        statuses[row.id] = row.status;
        return statuses;
      },
      {}
    );

    if (newlyApprovedRequest) setExpandedRowId(newlyApprovedRequest.id);
  }, [group.rows]);

  return (
    <div className="transportPersonaDetailContainer" onClick={(e) => e.stopPropagation()}>
      {group.rows.map((row) => {
        const isSelected = selectedRowId === row.id;
        const isValid = isWithinRange(row.validFromTs, row.validToTs, row.validFrom, row.validTo);
        // Sólo se puede usar una autorización aprobada y vigente. Las utilizadas, vencidas y
        // canceladas se muestran para que el guardia entienda por qué no están disponibles.
        const isSelectable = row.status === "approved" && isValid;
        // Mientras está pendiente listamos todos los managers notificados con su estado
        // individual; una vez aprobada sólo queda el anfitrión.
        const showManagerStatus = row.status === "pending" && row.people.length > 1;
        // Aprobada pero vencida: no es utilizable, así que no la pintamos como si lo fuera.
        const rowStatusClass =
          row.status === "approved" && !isValid ? "" : ROW_STATUS_CLASSES[row.status] ?? "";
        // Las solicitudes aprobadas traen datos propios (patente, dock, quién autorizó) que
        // valen la pena mostrar aparte: se comportan como las tarjetas de egreso, con el cuerpo
        // desplegando el detalle y el checkbox como único responsable de seleccionar.
        const isRequestAccordion = row.kind === "request" && row.status === "approved";
        const isExpanded = expandedRowId === row.id;
        const isApprovedByGuard = row.guardApproval.approvedByGuard;
        // Una solicitud es por un camión puntual: el cupo ("0/1", "1 lugar disp.") no dice nada
        // que la fila no diga ya. Sólo tiene sentido en las preautorizaciones, que son paraguas —
        // y en el pedido de dock ya aprobado que se fusionó con una, porque ahí el cupo que se
        // muestra es el del paraguas. Mientras el dock sigue pendiente no se anuncia.
        const showTruckCapacity =
          row.totalTrucks > 0 &&
          (row.kind !== "request" || (row.isMergedDockRequest && row.status === "approved"));
        // Ídem las observaciones: las de una solicitud están dirigidas al autorizador y no le
        // sirven al guardia. Las que valen son las de la preautorización, propias si la fila es el
        // paraguas o heredadas si es el pedido de dock fusionado con él.
        const observations = row.isMergedDockRequest
          ? row.parentObservations
          : row.kind !== "request"
          ? row.gateObservations
          : null;

        const handleRowClick = () => {
          if (disabled) return;
          if (isRequestAccordion) {
            setExpandedRowId(isExpanded ? null : row.id);
            return;
          }
          if (isSelectable) onSelectRow(row.id);
        };

        const handleCheckboxClick = (e: React.MouseEvent) => {
          if (!isRequestAccordion) return;
          // En estas filas el click del cuerpo despliega, así que seleccionar es cosa del
          // checkbox: hay que frenar la propagación para no desplegar además.
          e.stopPropagation();
          if (!disabled && isSelectable) onSelectRow(row.id);
        };

        return (
          <div
            key={row.id}
            className={`transportPersonaRow ${rowStatusClass} ${
              isSelected ? "transportPersonaRowSelected" : ""
            }`}
            onClick={handleRowClick}
            style={isSelectable || isRequestAccordion ? undefined : { cursor: "default" }}
          >
            {/* El padding vive acá y no en la fila, para que el detalle desplegable ocupe el
                ancho completo igual que en las tarjetas de egreso. */}
            <div className="transportPersonaRowHeaderArea">
              <div className="transportPersonaTopRow">
                <div className="transportPersonaLeft">
                  {isSelectable && (
                    <div className="transportPersonaCheckbox" onClick={handleCheckboxClick}>
                      {isSelected ? (
                        <CheckBoxIcon style={{ color: "#2be65e", fontSize: "28px" }} />
                      ) : (
                        <CheckBoxOutlineBlankIcon style={{ color: "#003350", fontSize: "28px" }} />
                      )}
                    </div>
                  )}
                  <div className="transportPersonaInfo">
                    {/* La aprobó el guardia: no hay anfitrión, y los managers que figuran son los
                        que fueron notificados y nunca respondieron. Mostrarlos confundiría. */}
                    {isApprovedByGuard ? (
                      <span className="transportPersonaNamePhone">Autorizado por guardia</span>
                    ) : (
                      <>
                        {row.people.length === 0 && (
                          <span className="transportPersonaNamePhone">
                            Sin autorizador asignado
                          </span>
                        )}
                        {row.people.length > 0 &&
                          !showManagerStatus &&
                          row.people.map((person) => (
                            <span key={person.user} className="transportPersonaNamePhone">
                              {person.name} - Tel: {person.phone}
                            </span>
                          ))}
                        {showManagerStatus && (
                          <Button
                            variant="outline"
                            size="small"
                            onClick={() => setAuthorizersModalRow(row)}
                          >
                            Ver listado de autorizadores {`(${row.people.length})`}
                          </Button>
                        )}
                      </>
                    )}
                    {row.status === "rejected" && (
                      <span className="transportPersonaMotivoRechazo">
                        Motivo de rechazo: {row.rejectionReason || "-"}
                      </span>
                    )}
                  </div>

                  {/* El dock también vive en el acordeón, pero el guardia necesita verlo sin
                      tener que desplegar la fila. */}
                  {hasDock(row.dock) && <Badge variant="brown" label={`Dock: ${row.dock}`} />}

                  {row.status === "pending" && onRegistrarPorOtroMedio && (
                    <Button
                      variant="outline"
                      size="small"
                      className="transportRegistrarOtroMedioButton transportRegistrarOtroMedioButtonInline"
                      onClick={() => onRegistrarPorOtroMedio(row.id)}
                    >
                      Registrar autorización por otro medio
                    </Button>
                  )}
                </div>

                <div className="transportPersonaRight">
                  {showTruckCapacity && (
                    <div className="transportPersonaTruckMeta">
                      <LocalShippingOutlinedIcon fontSize="small" style={{ color: "#94a3b8" }} />
                      <span>
                        {row.usedTrucks}/{row.totalTrucks}
                      </span>
                    </div>
                  )}

                  {row.operationLabel && (
                    <Badge
                      variant={row.operationLabel === "Descarga" ? "yellow" : "blue"}
                      label={row.operationLabel}
                    />
                  )}

                  {/* Un pedido de dock se muestra fusionado con su preautorización: no es ni una
                      cosa ni la otra, así que lleva su propia etiqueta. Cuando además está
                      fusionado, el badge del paraguas acompaña al del pedido: la tarjeta habla por
                      los dos. */}
                  {row.isDockRequest ? (
                    <>
                      <Badge variant="purple" label="Solicitud de dock" />
                      {row.isMergedDockRequest && (
                        <Badge variant="purple" label="Preautorización" />
                      )}
                    </>
                  ) : (
                    <Badge
                      variant={row.kind === "request" ? "teal" : "purple"}
                      label={row.kind === "request" ? "Solicitud" : "Preautorización"}
                    />
                  )}

                  {showTruckCapacity && (
                    <span className="transportLugaresDispCapsule">
                      {row.availableSlots}{" "}
                      {row.availableSlots === 1 ? "lugar disp." : "lugares disp."}
                    </span>
                  )}

                  {/* Las preautorizaciones suelen no traer patente; cuando la autorización es de
                      un vehículo puntual se muestra igual que en las tarjetas de egreso. */}
                  {row.vehiclePlate && (
                    <span className="transportPatenteChip">{row.vehiclePlate}</span>
                  )}

                  {isRequestAccordion && (
                    <ExpandMoreIcon
                      style={{
                        transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                        color: "#64748B",
                      }}
                    />
                  )}
                </div>
              </div>

              {row.status === "approved" &&
                row.totalTrucks > 0 &&
                (row.kind === "authorization" || row.isMergedDockRequest) && (
                  <div className="transportPersonaBottomRow">
                    <div className="transportProgressHeader">
                      <span className="transportProgressLabel">Uso de camiones</span>
                      <span className="transportProgressValue">{row.truckUsagePct}%</span>
                    </div>
                    <div className="transportProgressBarTrack">
                      <div
                        className="transportProgressBarFill"
                        style={{ width: `${row.truckUsagePct}%` }}
                      />
                    </div>
                  </div>
                )}

              <VigenciaRow row={row} isValid={isValid} onResendRow={onResendRow} />

              {observations && (
                <div className="transportObsBox">
                  <span className="transportObsBoxLabel">
                    {row.isMergedDockRequest
                      ? "Observaciones de la preautorización"
                      : "Observaciones para portería"}
                  </span>
                  <span className="transportObsBoxText">{observations}</span>
                </div>
              )}
            </div>

            {isRequestAccordion && (
              <div className={`transportAuthCardAccordionContent ${isExpanded ? "open" : ""}`}>
                <div className="transportAuthCardAccordionInner">
                  <SolicitudDetalle row={row} />
                </div>
              </div>
            )}
          </div>
        );
      })}

      <AutorizadoresModal
        open={!!authorizersModalRow}
        onClose={() => setAuthorizersModalRow(null)}
        people={authorizersModalRow?.people ?? []}
      />
    </div>
  );
};

export default AutorizacionDetail;
